// ============================================
// Hemologix Master Formulary — Tier 2 Rx Seed
// 32 Rx products (catalogNumber 30–61)
// ============================================

import type { Product } from '../../types/formulary';

export const SEED_RX: Product[] = [
  // ── 30. Metformin ER ───────────────────────────
  {
    id: 'rx-metformin-er',
    catalogNumber: 30,
    name: 'Metformin ER',
    ingredients: 'Metformin hydrochloride extended-release',
    category: 'rx',
    subcategory: 'weight_metabolic',
    tier: 2,
    route: 'oral',
    dosage: '500–1000 mg daily with evening meal',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'fasting_insulin', condition: 'above_functional', threshold: 10, description: 'Fasting insulin >10 uIU/mL' },
      { biomarker: 'fasting_glucose', condition: 'above_functional', threshold: 100, description: 'Fasting glucose >100 mg/dL' },
      { biomarker: 'hba1c', condition: 'above_functional', threshold: 5.5, description: 'HbA1c >5.5%' },
    ],
    targetBiomarkers: ['fasting_insulin', 'fasting_glucose', 'hba1c', 'HOMA-IR'],
    targetGoals: ['body_composition', 'longevity', 'cardiovascular'],
    pairsWith: ['rx-semaglutide', 'rx-tirzepatide'],
    contraindications: ['eGFR <30 mL/min', 'Hepatic impairment / liver disease', 'Metabolic acidosis', 'History of lactic acidosis'],
    drugInteractions: ['Carbonic anhydrase inhibitors (topiramate, zonisamide) — increased lactic acidosis risk', 'IV contrast dye — hold 48 h before/after', 'Excessive alcohol — lactic acidosis risk'],
    allergenFlags: [],
    pregnancyCategory: 'B',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Fasting glucose q3mo', 'HbA1c q3mo', 'eGFR q6mo', 'Vitamin B12 annually'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Metformin improves insulin sensitivity, reduces hepatic glucose output, and has demonstrated longevity and cardioprotective benefits in observational and interventional studies.',
    citations: ['Diabetes Prevention Program Research Group. N Engl J Med 2002;346:393-403', 'Bannister CA et al. Diabetes Obes Metab 2014;16(12):1165-73'],
    gender: 'any',
    clinicalNotes: 'Titrate slowly to minimize GI side effects. Extended-release formulation preferred for tolerability. Take with food.',
    rationale: 'Fasting insulin and/or glucose above functional thresholds indicate early insulin resistance. Metformin ER improves insulin sensitivity and supports metabolic optimization.',
    pricing: { supplierCost: '$4–8/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 30,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 31. Testosterone Cypionate ─────────────────
  {
    id: 'rx-testosterone-cypionate',
    catalogNumber: 31,
    name: 'Testosterone Cypionate',
    ingredients: 'Testosterone cypionate in cottonseed oil',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'im',
    dosage: '100–200 mg/week IM or SubQ',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', threshold: 400, description: 'Total T <400 ng/dL' },
      { biomarker: 'free_testosterone', condition: 'below_functional', threshold: 10, description: 'Free T <10 pg/mL' },
    ],
    targetBiomarkers: ['total_testosterone', 'free_testosterone', 'SHBG', 'estradiol', 'hematocrit'],
    targetGoals: ['hormonal', 'energy', 'body_composition', 'sexual_health', 'cognition'],
    pairsWith: ['rx-anastrozole', 'rx-hcg', 'rx-dhea'],
    contraindications: ['Breast cancer (male)', 'Prostate cancer', 'Polycythemia (hematocrit >54%)', 'Severe untreated sleep apnea', 'Uncontrolled heart failure'],
    drugInteractions: ['Anticoagulants (warfarin) — enhanced effect, monitor INR', 'Insulin / oral hypoglycemics — may decrease glucose; monitor', 'Corticosteroids — increased edema risk'],
    allergenFlags: ['Cottonseed oil (carrier)'],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Total T & Free T q3mo (first year), then q6mo', 'Hematocrit q3–6mo', 'PSA at baseline and q6–12mo', 'Lipid panel q6mo', 'Estradiol q3–6mo', 'Liver function baseline'],
    evidenceLevel: 'strong',
    evidenceSummary: 'TRT restores testosterone to physiologic levels, improving energy, body composition, libido, bone density, and mood in hypogonadal men.',
    citations: ['Bhasin S et al. J Clin Endocrinol Metab 2018;103(5):1715-44', 'Snyder PJ et al. N Engl J Med 2016;374:611-24'],
    gender: 'male',
    clinicalNotes: 'Split to 2x/week dosing for stable levels. SubQ injection is equally effective with less scarring. Monitor hematocrit closely — phlebotomy if >54%.',
    rationale: 'Total testosterone and/or free testosterone below functional range with clinical symptoms. TRT restores physiologic levels to improve energy, cognition, libido, and body composition.',
    pricing: { supplierCost: '$30–60/mo', monthlyPrice: '$99/mo' },
    status: 'active',
    sortOrder: 31,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 32. Testosterone Cream (Female) ────────────
  {
    id: 'rx-testosterone-cream-female',
    catalogNumber: 32,
    name: 'Testosterone Cream',
    ingredients: 'Testosterone in compounded topical cream base',
    category: 'rx',
    subcategory: 'hormone_female',
    tier: 2,
    route: 'topical',
    dosage: '0.5–1 mg/day applied to inner wrist or labia',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', threshold: 20, description: 'Female Total T <20 ng/dL' },
      { biomarker: 'free_testosterone', condition: 'below_functional', threshold: 1.5, description: 'Female Free T <1.5 pg/mL' },
    ],
    targetBiomarkers: ['total_testosterone', 'free_testosterone', 'SHBG'],
    targetGoals: ['sexual_health', 'cognition', 'energy'],
    pairsWith: ['rx-estradiol-patch', 'rx-progesterone-prometrium', 'rx-dhea'],
    contraindications: ['Pregnancy or planning pregnancy', 'Hormone-sensitive cancer', 'Polycythemia', 'Severe acne / hirsutism (relative)'],
    drugInteractions: ['Anticoagulants — enhanced effect', 'Insulin — may alter glucose control'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Total T & Free T at 6 weeks then q3–6mo', 'SHBG q6mo', 'Lipid panel q6mo', 'Monitor for virilization signs'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Low-dose testosterone in women improves sexual desire, arousal, and orgasm frequency. May also support cognitive function and wellbeing in postmenopausal women.',
    citations: ['Davis SR et al. Lancet Diabetes Endocrinol 2019;7(10):754-66', 'Achilli C et al. Hum Reprod Update 2017;23(4):361-78'],
    gender: 'female',
    clinicalNotes: 'Low-dose compounded cream for female libido and cognition support. Avoid transfer to partners/children. Monitor for acne, hirsutism, voice changes.',
    rationale: 'Female testosterone below functional range with symptoms of low libido and/or cognitive decline. Low-dose topical testosterone supports sexual health and cognition.',
    pricing: { supplierCost: '$25–45/mo', monthlyPrice: '$79/mo' },
    status: 'active',
    sortOrder: 32,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 33. Estradiol Patch ────────────────────────
  {
    id: 'rx-estradiol-patch',
    catalogNumber: 33,
    name: 'Estradiol Patch',
    ingredients: '17-beta estradiol transdermal system',
    category: 'rx',
    subcategory: 'hrt_female',
    tier: 2,
    route: 'patch',
    dosage: '0.025–0.1 mg/day transdermal, changed 1–2x/week',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'estradiol', condition: 'below_functional', threshold: 50, description: 'Estradiol <50 pg/mL in postmenopausal female' },
    ],
    targetBiomarkers: ['estradiol', 'FSH', 'LH'],
    targetGoals: ['hormonal', 'sleep', 'cognition', 'cardiovascular', 'longevity'],
    pairsWith: ['rx-progesterone-prometrium', 'rx-testosterone-cream-female', 'rx-dhea'],
    contraindications: ['History of breast cancer', 'History of DVT/PE or active thromboembolic disease', 'Undiagnosed vaginal bleeding', 'Active liver disease', 'Known protein C/S deficiency or Factor V Leiden (relative)'],
    drugInteractions: ['CYP3A4 inducers (rifampin, carbamazepine, phenytoin) — reduced estradiol levels', 'Thyroid hormone — may increase TBG, requiring thyroid dose adjustment', 'Tamoxifen — antagonistic interaction'],
    allergenFlags: ['Adhesive sensitivity (patch)'],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Estradiol level at 4–6 weeks then q6mo', 'FSH q6mo', 'Mammogram annually', 'Endometrial evaluation if breakthrough bleeding'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Transdermal estradiol is the preferred delivery for HRT, associated with lower VTE risk than oral estrogen. Relieves vasomotor symptoms and protects bone density and cardiovascular health.',
    citations: ['The NAMS 2022 Hormone Therapy Position Statement', 'Canonico M et al. Circulation 2007;115(7):840-45'],
    gender: 'female',
    clinicalNotes: 'Transdermal preferred over oral to avoid first-pass hepatic metabolism and minimize clotting risk. Must use with progesterone if uterus is intact.',
    rationale: 'Postmenopausal estradiol below functional range. Transdermal estradiol provides physiologic replacement with lower thromboembolic risk than oral formulations.',
    pricing: { supplierCost: '$20–50/mo', monthlyPrice: '$69/mo' },
    status: 'active',
    sortOrder: 33,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 34. Progesterone (Prometrium) ──────────────
  {
    id: 'rx-progesterone-prometrium',
    catalogNumber: 34,
    name: 'Progesterone (Prometrium)',
    ingredients: 'Micronized progesterone in peanut oil',
    category: 'rx',
    subcategory: 'hrt_female',
    tier: 2,
    route: 'oral',
    dosage: '100–200 mg at bedtime',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'progesterone', condition: 'below_functional', threshold: 1, description: 'Progesterone below functional range' },
    ],
    targetBiomarkers: ['progesterone', 'estradiol'],
    targetGoals: ['hormonal', 'sleep'],
    pairsWith: ['rx-estradiol-patch', 'rx-testosterone-cream-female'],
    contraindications: ['Peanut allergy (contains peanut oil)', 'Undiagnosed vaginal bleeding', 'History of breast cancer (relative)', 'Active thromboembolic disease', 'Severe hepatic impairment'],
    drugInteractions: ['CYP3A4 inducers (rifampin, carbamazepine) — decreased progesterone levels', 'CNS depressants / alcohol — enhanced sedation', 'Ketoconazole — increased progesterone levels'],
    allergenFlags: ['Peanut oil'],
    pregnancyCategory: 'B',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Progesterone level q6mo', 'Endometrial assessment annually if on HRT', 'Sleep quality assessment'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Micronized progesterone provides endometrial protection when combined with estradiol in women with an intact uterus. Also promotes sleep through GABAergic metabolites.',
    citations: ['The NAMS 2022 Hormone Therapy Position Statement', 'Hitchcock CL, Prior JC. Menopause 2012;19(8):886-93'],
    gender: 'female',
    clinicalNotes: 'Take at bedtime — promotes sleep via allopregnanolone (GABA-A agonist metabolite). MUST be used with estradiol in women with intact uterus to prevent endometrial hyperplasia. Contraindicated with peanut allergy.',
    rationale: 'Progesterone replacement for hormonal balance and endometrial protection in postmenopausal women on estradiol therapy. Bedtime dosing leverages natural sedative properties.',
    pricing: { supplierCost: '$15–30/mo', monthlyPrice: '$49/mo' },
    status: 'active',
    sortOrder: 34,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 35. DHEA ───────────────────────────────────
  {
    id: 'rx-dhea',
    catalogNumber: 35,
    name: 'DHEA',
    ingredients: 'Dehydroepiandrosterone (micronized)',
    category: 'rx',
    subcategory: 'hormone_precursor',
    tier: 2,
    route: 'oral',
    dosage: '25–50 mg/day (male), 10–25 mg/day (female)',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'dhea_s', condition: 'below_functional', description: 'DHEA-S below age-adjusted functional range' },
    ],
    targetBiomarkers: ['dhea_s', 'total_testosterone', 'estradiol'],
    targetGoals: ['hormonal', 'energy', 'cognition', 'immune', 'longevity'],
    pairsWith: ['rx-pregnenolone', 'rx-testosterone-cypionate', 'rx-testosterone-cream-female'],
    contraindications: ['Hormone-sensitive cancers (breast, prostate, ovarian)', 'PCOS with elevated androgens (female)', 'Severe hepatic impairment'],
    drugInteractions: ['Anticoagulants — may enhance effect', 'Insulin / oral hypoglycemics — monitor glucose', 'Aromatase inhibitors — pharmacologic interaction'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['DHEA-S at 6 weeks then q6mo', 'Testosterone (total & free) q6mo', 'Estradiol q6mo in females', 'Lipid panel q6mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'DHEA supplementation restores adrenal hormone levels that decline with age, supporting immune function, bone density, and overall wellbeing.',
    citations: ['Traish AM et al. J Sex Med 2011;8(11):2960-82', 'Rutkowski K et al. Eur J Clin Invest 2014;44(4):403-14'],
    gender: 'any',
    clinicalNotes: 'Dose by sex: 25–50 mg male, 10–25 mg female. Monitor downstream conversion to testosterone and estradiol. Reduce dose if androgenic side effects develop.',
    rationale: 'DHEA-S below functional range indicating adrenal decline. Replacement supports hormone precursor pools, immune function, and metabolic health.',
    pricing: { supplierCost: '$8–15/mo', monthlyPrice: '$39/mo' },
    status: 'active',
    sortOrder: 35,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 36. Levothyroxine ─────────────────────────
  {
    id: 'rx-levothyroxine',
    catalogNumber: 36,
    name: 'Levothyroxine',
    ingredients: 'Levothyroxine sodium (synthetic T4)',
    category: 'rx',
    subcategory: 'thyroid',
    tier: 2,
    route: 'oral',
    dosage: '25–200 mcg daily on empty stomach',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'TSH', condition: 'above_functional', threshold: 3.0, description: 'TSH >3.0 mIU/L' },
      { biomarker: 'free_t4', condition: 'below_functional', description: 'Free T4 below functional range' },
    ],
    targetBiomarkers: ['TSH', 'free_t4', 'free_t3'],
    targetGoals: ['energy', 'body_composition', 'cognition'],
    pairsWith: ['rx-liothyronine-t3', 'rx-armour-thyroid'],
    contraindications: ['Untreated adrenal insufficiency', 'Thyrotoxicosis', 'Acute myocardial infarction (relative)'],
    drugInteractions: ['Calcium, iron, antacids — separate by 4 hours (reduce absorption)', 'Warfarin — increased anticoagulant effect; monitor INR', 'PPIs, sucralfate — decreased absorption', 'Estrogen therapy — may increase TBG, requiring dose increase', 'Carbamazepine, phenytoin — increased T4 metabolism'],
    allergenFlags: [],
    pregnancyCategory: 'A',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['TSH at 6–8 weeks after initiation or dose change', 'Free T4 and Free T3 q6–8 weeks until stable', 'TSH q6mo once stable', 'Heart rate and symptoms at each visit'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Levothyroxine is the standard of care for hypothyroidism, restoring TSH and free T4 to target ranges with well-established efficacy and safety.',
    citations: ['Jonklaas J et al. Thyroid 2014;24(12):1670-751 (ATA Guidelines)', 'Garber JR et al. Thyroid 2012;22(12):1200-35'],
    gender: 'any',
    clinicalNotes: 'Take on empty stomach 30–60 min before food. Separate from calcium, iron, and coffee by 4 hours. Start low and titrate based on TSH at 6–8 week intervals.',
    rationale: 'Elevated TSH with low free T4 indicates hypothyroidism. Levothyroxine restores thyroid hormone to functional range, supporting energy, metabolism, and cognition.',
    pricing: { supplierCost: '$4–12/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 36,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 37. Liothyronine (T3) ─────────────────────
  {
    id: 'rx-liothyronine-t3',
    catalogNumber: 37,
    name: 'Liothyronine (T3)',
    ingredients: 'Liothyronine sodium (synthetic T3)',
    category: 'rx',
    subcategory: 'thyroid',
    tier: 2,
    route: 'oral',
    dosage: '5–25 mcg daily, often split AM/PM',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      { biomarker: 'free_t3', condition: 'below_functional', description: 'Free T3 below functional range' },
    ],
    targetBiomarkers: ['free_t3', 'TSH', 'reverse_t3'],
    targetGoals: ['energy', 'body_composition', 'cognition'],
    pairsWith: ['rx-levothyroxine'],
    contraindications: ['Untreated adrenal insufficiency', 'Thyrotoxicosis', 'Acute cardiovascular disease'],
    drugInteractions: ['Calcium, iron, antacids — separate by 4 hours', 'Warfarin — increased anticoagulant effect', 'Beta-blockers — may need dose adjustment', 'Digoxin — T3 may reduce digoxin levels'],
    allergenFlags: [],
    pregnancyCategory: 'A',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Free T3 at 4–6 weeks then q3mo', 'TSH q6–8 weeks until stable', 'Heart rate and rhythm monitoring', 'Bone density consideration in long-term use'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Liothyronine added to T4 therapy can improve symptoms in patients with persistent hypothyroid symptoms despite normal TSH on levothyroxine alone.',
    citations: ['Wiersinga WM et al. Eur Thyroid J 2012;1(2):55-71', 'Hoang TD et al. J Clin Endocrinol Metab 2013;98(5):1982-90'],
    gender: 'any',
    clinicalNotes: 'Short half-life — split dose AM and early PM for steady levels. Often used as add-on to T4. Start at 5 mcg and titrate slowly. Monitor heart rate — reduce dose if tachycardia.',
    rationale: 'Low free T3 despite adequate T4 replacement suggests poor T4-to-T3 conversion. Low-dose T3 supplementation can resolve persistent fatigue, brain fog, and metabolic stagnation.',
    pricing: { supplierCost: '$10–25/mo', monthlyPrice: '$45/mo' },
    status: 'active',
    sortOrder: 37,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 38. Armour Thyroid ─────────────────────────
  {
    id: 'rx-armour-thyroid',
    catalogNumber: 38,
    name: 'Armour Thyroid',
    ingredients: 'Desiccated porcine thyroid (T4 + T3)',
    category: 'rx',
    subcategory: 'thyroid',
    tier: 2,
    route: 'oral',
    dosage: '30–120 mg daily on empty stomach',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'TSH', condition: 'above_functional', threshold: 3.0, description: 'TSH >3.0 mIU/L' },
      { biomarker: 'free_t3', condition: 'below_functional', description: 'Free T3 below functional range' },
      { biomarker: 'free_t4', condition: 'below_functional', description: 'Free T4 below functional range' },
    ],
    targetBiomarkers: ['TSH', 'free_t4', 'free_t3', 'reverse_t3'],
    targetGoals: ['energy', 'body_composition', 'cognition'],
    pairsWith: [],
    contraindications: ['Untreated adrenal insufficiency', 'Thyrotoxicosis', 'Pork allergy', 'Acute cardiovascular disease'],
    drugInteractions: ['Calcium, iron, antacids — separate by 4 hours', 'Warfarin — increased anticoagulant effect', 'Beta-blockers — may need dose adjustment', 'Estrogen therapy — may require dose increase'],
    allergenFlags: ['Porcine-derived'],
    pregnancyCategory: 'A',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['TSH at 6–8 weeks after initiation or dose change', 'Free T4 and Free T3 q6–8 weeks until stable', 'TSH, Free T3, Free T4 q6mo once stable', 'Heart rate monitoring'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Natural desiccated thyroid provides both T4 and T3 in a fixed ratio. Some patients report better symptom resolution than with synthetic T4 alone.',
    citations: ['Hoang TD et al. J Clin Endocrinol Metab 2013;98(5):1982-90', 'ATA Guidelines 2014'],
    gender: 'any',
    clinicalNotes: 'Contains both T4 and T3 in ~4:1 ratio. Take on empty stomach. Some patients prefer NDT over synthetic. Contraindicated with pork allergy. Lot-to-lot variability is a known limitation.',
    rationale: 'TSH above functional range with low free T3/T4 indicating hypothyroidism. Natural desiccated thyroid provides combined T4/T3 therapy for patients preferring or responding better to combination treatment.',
    pricing: { supplierCost: '$20–45/mo', monthlyPrice: '$59/mo' },
    status: 'active',
    sortOrder: 38,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 39. Semaglutide ───────────────────────────
  {
    id: 'rx-semaglutide',
    catalogNumber: 39,
    name: 'Semaglutide',
    ingredients: 'Semaglutide (GLP-1 receptor agonist)',
    category: 'rx',
    subcategory: 'glp1_weight',
    tier: 2,
    route: 'subq',
    dosage: '0.25 mg/week titrated to 2.4 mg/week over 16–20 weeks',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'BMI', condition: 'above_functional', threshold: 30, description: 'BMI ≥30' },
      { biomarker: 'fasting_insulin', condition: 'above_functional', threshold: 15, description: 'Fasting insulin >15 with BMI >27' },
    ],
    targetBiomarkers: ['BMI', 'fasting_insulin', 'fasting_glucose', 'hba1c', 'triglycerides'],
    targetGoals: ['body_composition', 'cardiovascular', 'longevity'],
    pairsWith: ['rx-metformin-er', 'rx-tirzepatide'],
    contraindications: ['Personal or family history of medullary thyroid carcinoma (MTC)', 'Multiple Endocrine Neoplasia type 2 (MEN2)', 'History of pancreatitis (relative)', 'Severe gastroparesis', 'Pregnancy or planning pregnancy'],
    drugInteractions: ['Insulin / sulfonylureas — increased hypoglycemia risk; reduce doses', 'Oral medications — slowed gastric emptying may alter absorption', 'Warfarin — monitor INR more frequently'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Weight q4 weeks', 'Fasting glucose and HbA1c q3mo', 'Lipid panel q3mo', 'Renal function q6mo', 'Thyroid palpation annually', 'Gallbladder symptoms assessment', 'Lean mass / body composition q3mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Semaglutide 2.4 mg weekly demonstrated 14.9% body weight reduction in STEP trials with significant improvements in cardiometabolic markers and 20% reduction in MACE events (SELECT trial).',
    citations: ['Wilding JPH et al. N Engl J Med 2021;384:989-1002 (STEP 1)', 'Lincoff AM et al. N Engl J Med 2023;389:2221-32 (SELECT)'],
    gender: 'any',
    clinicalNotes: 'Titrate slowly over 16–20 weeks to minimize GI side effects. Ensure adequate protein intake (1 g/kg) to preserve lean mass. Monitor for pancreatitis symptoms. Discontinue 2 months before planned pregnancy.',
    rationale: 'BMI above threshold and/or metabolic dysfunction. Semaglutide reduces appetite centrally and improves insulin sensitivity with proven cardiovascular benefit.',
    pricing: { supplierCost: '$150–300/mo', monthlyPrice: '$399/mo' },
    status: 'active',
    sortOrder: 39,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 40. Tirzepatide ───────────────────────────
  {
    id: 'rx-tirzepatide',
    catalogNumber: 40,
    name: 'Tirzepatide',
    ingredients: 'Tirzepatide (dual GIP/GLP-1 receptor agonist)',
    category: 'rx',
    subcategory: 'glp1_weight',
    tier: 2,
    route: 'subq',
    dosage: '2.5 mg/week titrated to 10–15 mg/week over 16–20 weeks',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'BMI', condition: 'above_functional', threshold: 30, description: 'BMI ≥30' },
      { biomarker: 'fasting_insulin', condition: 'above_functional', threshold: 15, description: 'Fasting insulin >15 with BMI >27' },
      { biomarker: 'hba1c', condition: 'above_functional', threshold: 6.0, description: 'HbA1c >6.0%' },
    ],
    targetBiomarkers: ['BMI', 'fasting_insulin', 'fasting_glucose', 'hba1c', 'triglycerides'],
    targetGoals: ['body_composition', 'cardiovascular', 'longevity'],
    pairsWith: ['rx-metformin-er'],
    contraindications: ['Personal or family history of medullary thyroid carcinoma (MTC)', 'Multiple Endocrine Neoplasia type 2 (MEN2)', 'History of pancreatitis (relative)', 'Severe gastroparesis', 'Pregnancy or planning pregnancy'],
    drugInteractions: ['Insulin / sulfonylureas — increased hypoglycemia risk; reduce doses', 'Oral medications — slowed gastric emptying may alter absorption timing', 'Oral contraceptives — use barrier method for 4 weeks after initiation or dose increase'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Weight q4 weeks', 'Fasting glucose and HbA1c q3mo', 'Lipid panel q3mo', 'Renal function q6mo', 'Thyroid palpation annually', 'Gallbladder symptoms assessment', 'Lean mass / body composition q3mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Tirzepatide demonstrated up to 22.5% body weight reduction in SURMOUNT-1. Dual GIP/GLP-1 mechanism provides superior glycemic control and weight loss compared to GLP-1 agonists alone.',
    citations: ['Jastreboff AM et al. N Engl J Med 2022;387:205-16 (SURMOUNT-1)', 'Frias JP et al. N Engl J Med 2021;385:503-15 (SURPASS-2)'],
    gender: 'any',
    clinicalNotes: 'Dual GIP/GLP-1 agonist — may achieve greater weight loss than semaglutide. Same titration caution. Ensure adequate protein intake. Monitor for pancreatitis and gallbladder disease.',
    rationale: 'BMI and/or metabolic markers above threshold. Dual incretin mechanism targets both GIP and GLP-1 pathways for superior weight management and glycemic control.',
    pricing: { supplierCost: '$200–400/mo', monthlyPrice: '$449/mo' },
    status: 'active',
    sortOrder: 40,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 41. Low Dose Naltrexone ───────────────────
  {
    id: 'rx-low-dose-naltrexone',
    catalogNumber: 41,
    name: 'Low Dose Naltrexone',
    ingredients: 'Naltrexone hydrochloride (compounded low-dose)',
    category: 'rx',
    subcategory: 'immune_inflammation',
    tier: 2,
    route: 'oral',
    dosage: '1.5–4.5 mg at bedtime',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'hs_crp', condition: 'above_functional', threshold: 1.0, description: 'hs-CRP >1.0 mg/L (chronic inflammation)' },
      { biomarker: 'ESR', condition: 'above_functional', description: 'ESR elevated above functional range' },
    ],
    targetBiomarkers: ['hs_crp', 'ESR', 'TNF_alpha', 'IL_6'],
    targetGoals: ['immune', 'energy', 'longevity'],
    pairsWith: [],
    contraindications: ['Current opioid use (any — will precipitate withdrawal)', 'Current opioid dependence', 'Acute hepatitis or liver failure', 'Concurrent use of opioid-containing medications (codeine, tramadol, hydrocodone)'],
    drugInteractions: ['Opioid analgesics — BLOCKS opioid effect; precipitates withdrawal', 'Opioid-containing cough suppressants', 'Immunosuppressants — potential interaction; use with caution', 'Disulfiram — hepatotoxicity risk'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['hs-CRP at 8–12 weeks then q6mo', 'Liver function at baseline and q6mo', 'Symptom assessment q3mo', 'Pain / inflammation score tracking'],
    evidenceLevel: 'emerging',
    evidenceSummary: 'LDN transiently blocks opioid receptors, upregulating endorphin production and modulating microglial activation. Emerging evidence supports use in autoimmune conditions, chronic pain, and systemic inflammation.',
    citations: ['Younger J et al. Pain Med 2013;14(5):775-81', 'Patten DK et al. BMJ Open Gastroenterol 2018;5(1):e000208'],
    gender: 'any',
    clinicalNotes: 'Compounded formulation required (standard naltrexone is 50 mg). Start at 1.5 mg and titrate up over 2–4 weeks. Take at bedtime. Vivid dreams may occur initially. MUST confirm no opioid use within 7–10 days.',
    rationale: 'Elevated inflammatory markers with chronic inflammation or autoimmune component. LDN modulates immune response and endorphin production to reduce systemic inflammation.',
    pricing: { supplierCost: '$20–40/mo', monthlyPrice: '$59/mo' },
    status: 'active',
    sortOrder: 41,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 42. Finasteride ───────────────────────────
  {
    id: 'rx-finasteride',
    catalogNumber: 42,
    name: 'Finasteride',
    ingredients: 'Finasteride (5-alpha reductase inhibitor, type II)',
    category: 'rx',
    subcategory: 'skin_hair',
    tier: 2,
    route: 'oral',
    dosage: '1 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'DHT', condition: 'above_functional', description: 'Elevated DHT with androgenetic alopecia' },
    ],
    targetBiomarkers: ['DHT', 'total_testosterone'],
    targetGoals: ['body_composition'],
    pairsWith: ['rx-minoxidil-oral', 'rx-dutasteride'],
    contraindications: ['Female patients (teratogenic — Category X in women)', 'Pregnancy or potential pregnancy in partner (handle with care)', 'Hepatic impairment (metabolized by CYP3A4)'],
    drugInteractions: ['No clinically significant drug interactions at 1 mg dose', 'PSA values will be reduced ~50% — adjust interpretation accordingly'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['PSA at baseline (adjust for 50% reduction)', 'DHT level at 3 months (optional)', 'Sexual function assessment q6mo', 'Mood assessment q6mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Finasteride 1 mg daily reduces scalp DHT by ~60%, slowing hair loss and promoting regrowth in 80–90% of men with androgenetic alopecia.',
    citations: ['Kaufman KD et al. J Am Acad Dermatol 1998;39(4 Pt 1):578-89', 'Zito PM et al. StatPearls 2024 - Finasteride'],
    gender: 'male',
    clinicalNotes: 'Effects take 3–6 months to appear and reverse upon discontinuation. Rare sexual side effects (1–2%): decreased libido, ED, reduced ejaculate volume. Post-finasteride syndrome is debated. Women of childbearing age must not handle crushed tablets.',
    rationale: 'DHT-mediated androgenetic alopecia in male patient. Finasteride inhibits 5-alpha reductase to reduce scalp DHT and halt hair loss progression.',
    pricing: { supplierCost: '$5–10/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 42,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 43. Minoxidil (Oral) ──────────────────────
  {
    id: 'rx-minoxidil-oral',
    catalogNumber: 43,
    name: 'Minoxidil (Oral)',
    ingredients: 'Minoxidil (oral low-dose)',
    category: 'rx',
    subcategory: 'skin_hair',
    tier: 2,
    route: 'oral',
    dosage: '2.5–5 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'DHT', condition: 'above_functional', description: 'Hair loss with or without elevated DHT' },
    ],
    targetBiomarkers: ['DHT'],
    targetGoals: ['body_composition'],
    pairsWith: ['rx-finasteride', 'rx-dutasteride', 'rx-spironolactone'],
    contraindications: ['Pheochromocytoma', 'Severe hypotension', 'Pulmonary hypertension associated with mitral stenosis', 'Severe renal impairment'],
    drugInteractions: ['Antihypertensives — additive hypotension', 'NSAIDs — may reduce antihypertensive effect', 'Guanethidine — severe orthostatic hypotension'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Blood pressure at baseline, 2 weeks, and q3mo', 'Heart rate at baseline and q3mo', 'Renal function at baseline', 'Monitor for pericardial effusion (rare at low dose)', 'Electrolytes q6mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Low-dose oral minoxidil promotes hair growth through vasodilation and upregulation of VEGF. Increasingly used off-label for hair loss at 2.5–5 mg with favorable safety profile.',
    citations: ['Randolph M, Tosti A. J Am Acad Dermatol 2021;84(3):737-46', 'Jimenez-Cauhe J et al. J Am Acad Dermatol 2021;84(6):1644-51'],
    gender: 'any',
    clinicalNotes: 'Low-dose oral route avoids topical irritation and improves compliance. May cause hypertrichosis (body hair growth). Monitor blood pressure closely in first month. Start at 2.5 mg.',
    rationale: 'Hair loss warranting systemic treatment. Oral minoxidil promotes follicular blood flow and hair regrowth with better compliance than topical formulations.',
    pricing: { supplierCost: '$8–15/mo', monthlyPrice: '$35/mo' },
    status: 'active',
    sortOrder: 43,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 44. Spironolactone ────────────────────────
  {
    id: 'rx-spironolactone',
    catalogNumber: 44,
    name: 'Spironolactone',
    ingredients: 'Spironolactone (aldosterone antagonist / anti-androgen)',
    category: 'rx',
    subcategory: 'hormone_female',
    tier: 2,
    route: 'oral',
    dosage: '50–100 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'above_functional', description: 'Elevated testosterone in female (androgen excess)' },
      { biomarker: 'DHT', condition: 'above_functional', description: 'Elevated DHT in female' },
    ],
    targetBiomarkers: ['total_testosterone', 'free_testosterone', 'DHT', 'potassium'],
    targetGoals: ['hormonal', 'body_composition'],
    pairsWith: ['rx-minoxidil-oral', 'rx-tretinoin'],
    contraindications: ['Hyperkalemia (K+ >5.0)', 'Severe renal impairment (eGFR <30)', 'Addison disease', 'Pregnancy (Category D — feminization of male fetus)', 'Concurrent use of potassium-sparing diuretics'],
    drugInteractions: ['ACE inhibitors / ARBs — hyperkalemia risk', 'Potassium supplements — hyperkalemia risk', 'NSAIDs — reduced diuretic effect and hyperkalemia', 'Digoxin — spironolactone may increase digoxin levels', 'Lithium — increased lithium toxicity risk'],
    allergenFlags: [],
    pregnancyCategory: 'D',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Potassium at baseline, 1 week, 1 month, then q3mo', 'Renal function (BUN/Cr) at baseline then q6mo', 'Blood pressure q3mo', 'Menstrual cycle regularity assessment'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Spironolactone is an effective anti-androgen for female acne, hirsutism, and PCOS-related androgen excess with decades of clinical evidence.',
    citations: ['Brown J et al. Cochrane Database Syst Rev 2009;(2):CD000194', 'Plovanich M et al. J Am Acad Dermatol 2015;73(3):456-63'],
    gender: 'female',
    clinicalNotes: 'Female only — anti-androgen effect. Monitor potassium closely, especially with ACE/ARB use. Effective contraception required (teratogenic). Full effect on acne/hirsutism takes 3–6 months.',
    rationale: 'Androgen excess in female patient (elevated testosterone, acne, hirsutism, or PCOS). Spironolactone blocks androgen receptors and reduces androgen production.',
    pricing: { supplierCost: '$5–12/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 44,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 45. Tadalafil ─────────────────────────────
  {
    id: 'rx-tadalafil',
    catalogNumber: 45,
    name: 'Tadalafil',
    ingredients: 'Tadalafil (PDE5 inhibitor)',
    category: 'rx',
    subcategory: 'sexual_male',
    tier: 2,
    route: 'oral',
    dosage: '5 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', description: 'ED symptoms with low or normal testosterone' },
    ],
    targetBiomarkers: ['total_testosterone', 'estradiol'],
    targetGoals: ['sexual_health', 'prostate', 'cardiovascular'],
    pairsWith: ['rx-testosterone-cypionate'],
    contraindications: ['Concurrent nitrate use (nitroglycerin, isosorbide) — LIFE-THREATENING HYPOTENSION', 'Concurrent alpha-blockers (relative — titrate carefully)', 'Severe hepatic impairment', 'Recent stroke or MI within 90 days', 'Unstable angina', 'Retinitis pigmentosa'],
    drugInteractions: ['Nitrates — ABSOLUTE CONTRAINDICATION; fatal hypotension', 'Alpha-blockers (tamsulosin, doxazosin) — severe hypotension; start low', 'CYP3A4 inhibitors (ketoconazole, ritonavir) — increased tadalafil levels', 'Antihypertensives — additive blood pressure lowering', 'Riociguat — contraindicated'],
    allergenFlags: [],
    pregnancyCategory: 'B',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Blood pressure at baseline and q6mo', 'Symptom assessment q3mo', 'Cardiovascular risk assessment annually', 'Vision / hearing changes — discontinue if sudden loss'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Tadalafil 5 mg daily provides continuous erectile function support with additional benefits for BPH/LUTS. Long half-life (17.5 h) allows daily dosing without timing constraints.',
    citations: ['Porst H et al. Eur Urol 2006;50(2):351-59', 'Oelke M et al. Eur Urol 2012;61(5):917-25'],
    gender: 'male',
    clinicalNotes: 'Daily 5 mg preferred over PRN for steady-state coverage and BPH benefit. MUST screen for nitrate use before prescribing — interaction is potentially fatal. Also improves lower urinary tract symptoms.',
    rationale: 'Erectile dysfunction and/or BPH symptoms. Daily low-dose tadalafil provides continuous PDE5 inhibition supporting erectile function and urinary flow.',
    pricing: { supplierCost: '$8–20/mo', monthlyPrice: '$39/mo' },
    status: 'active',
    sortOrder: 45,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 46. Sildenafil ────────────────────────────
  {
    id: 'rx-sildenafil',
    catalogNumber: 46,
    name: 'Sildenafil',
    ingredients: 'Sildenafil citrate (PDE5 inhibitor)',
    category: 'rx',
    subcategory: 'sexual_male',
    tier: 2,
    route: 'oral',
    dosage: '20–100 mg as needed, 30–60 min before activity',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', description: 'ED symptoms with low or normal testosterone' },
    ],
    targetBiomarkers: ['total_testosterone'],
    targetGoals: ['sexual_health'],
    pairsWith: ['rx-testosterone-cypionate'],
    contraindications: ['Concurrent nitrate use — LIFE-THREATENING HYPOTENSION', 'Concurrent alpha-blockers (relative — use caution)', 'Severe hepatic impairment', 'Recent stroke or MI within 90 days', 'Hypotension (SBP <90)', 'Retinitis pigmentosa'],
    drugInteractions: ['Nitrates — ABSOLUTE CONTRAINDICATION; fatal hypotension', 'Alpha-blockers — severe orthostatic hypotension', 'CYP3A4 inhibitors (ketoconazole, ritonavir, erythromycin) — increased sildenafil levels; reduce dose', 'Antihypertensives — additive BP lowering', 'Riociguat — contraindicated'],
    allergenFlags: [],
    pregnancyCategory: 'B',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Blood pressure at baseline', 'Cardiovascular risk assessment', 'Vision / hearing — discontinue if sudden loss', 'Symptom assessment q6mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Sildenafil is the original PDE5 inhibitor with extensive safety and efficacy data for ED. Effective in 60–80% of men across etiologies.',
    citations: ['Goldstein I et al. N Engl J Med 1998;338:1397-404', 'Hatzimouratidis K et al. Eur Urol 2010;57(5):804-14'],
    gender: 'male',
    clinicalNotes: 'PRN dosing — take 30–60 min before activity. Avoid high-fat meals (delays absorption). Shorter half-life than tadalafil (4–5 h). MUST screen for nitrate use. Start at 50 mg, adjust based on response.',
    rationale: 'Erectile dysfunction requiring PRN treatment. Sildenafil provides on-demand PDE5 inhibition with well-established efficacy and safety profile.',
    pricing: { supplierCost: '$5–15/mo', monthlyPrice: '$35/mo' },
    status: 'active',
    sortOrder: 46,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 47. PT-141 (Bremelanotide) ────────────────
  {
    id: 'rx-pt141-bremelanotide',
    catalogNumber: 47,
    name: 'PT-141 (Bremelanotide)',
    ingredients: 'Bremelanotide acetate (melanocortin receptor agonist)',
    category: 'rx',
    subcategory: 'sexual_female',
    tier: 2,
    route: 'subq',
    dosage: '1.75 mg SubQ PRN, at least 45 min before activity',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', description: 'Hypoactive sexual desire disorder (HSDD) in premenopausal female' },
    ],
    targetBiomarkers: ['total_testosterone'],
    targetGoals: ['sexual_health'],
    pairsWith: ['rx-testosterone-cream-female'],
    contraindications: ['Uncontrolled hypertension', 'Cardiovascular disease', 'Concurrent use with naltrexone (standard dose)', 'More than 1 dose in 24 hours', 'More than 8 doses per month'],
    drugInteractions: ['Naltrexone (standard dose 50 mg) — decreased PT-141 efficacy', 'Oral medications — slows GI motility; separate by 1 hour', 'Antihypertensives — monitor for BP fluctuations'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Blood pressure at baseline and periodic', 'Nausea assessment (most common side effect)', 'Skin pigmentation monitoring', 'Symptom / desire assessment q3mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Bremelanotide (Vyleesi) is FDA-approved for HSDD in premenopausal women, activating melanocortin-4 receptors in the CNS to increase sexual desire.',
    citations: ['Kingsberg SA et al. Obstet Gynecol 2019;134(5):899-908 (RECONNECT)', 'Simon JA et al. Menopause 2019;26(10):1121-30'],
    gender: 'female',
    clinicalNotes: 'PRN use only — max 1 dose per 24 hours, max 8 per month. Common side effect: nausea (40%) — typically resolves. May cause transient BP increase and skin hyperpigmentation with frequent use.',
    rationale: 'Hypoactive sexual desire disorder in premenopausal female. PT-141 activates central melanocortin pathways to restore sexual desire.',
    pricing: { supplierCost: '$40–80/dose', monthlyPrice: '$199/mo' },
    status: 'active',
    sortOrder: 47,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 48. Anastrozole ───────────────────────────
  {
    id: 'rx-anastrozole',
    catalogNumber: 48,
    name: 'Anastrozole',
    ingredients: 'Anastrozole (aromatase inhibitor)',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'oral',
    dosage: '0.25–0.5 mg 2x/week',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'estradiol', condition: 'above_functional', threshold: 40, description: 'Estradiol >40 pg/mL in male on TRT' },
    ],
    targetBiomarkers: ['estradiol', 'total_testosterone', 'free_testosterone'],
    targetGoals: ['hormonal', 'body_composition'],
    pairsWith: ['rx-testosterone-cypionate'],
    contraindications: ['Premenopausal females (off-label context)', 'Severe hepatic impairment', 'Severe osteoporosis (long-term AI use reduces bone density)'],
    drugInteractions: ['Tamoxifen — reduces anastrozole plasma levels', 'Estrogen-containing therapies — antagonistic interaction', 'CYP3A4 inducers — may reduce efficacy'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Estradiol at 4–6 weeks then q3mo', 'Total T and Free T q3mo', 'Bone density (DEXA) if long-term use >1 year', 'Lipid panel q6mo (AIs can worsen lipids)'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Low-dose anastrozole effectively reduces estradiol in men on TRT who experience aromatization, preventing gynecomastia, water retention, and mood changes.',
    citations: ['Leder BZ et al. J Clin Endocrinol Metab 2004;89(3):1174-80', 'Burnett-Bowie SA et al. J Clin Endocrinol Metab 2009;94(3):906-13'],
    gender: 'male',
    clinicalNotes: 'Use only when estradiol is confirmed elevated — do NOT use prophylactically. Over-suppression of E2 causes joint pain, low libido, and bone loss. Target E2 20–35 pg/mL in males. Low-dose 2x/week preferred.',
    rationale: 'Elevated estradiol in male patient on TRT indicating excess aromatization. Low-dose anastrozole reduces estrogen conversion while preserving adequate E2 for bone and cardiovascular health.',
    pricing: { supplierCost: '$8–15/mo', monthlyPrice: '$35/mo' },
    status: 'active',
    sortOrder: 48,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 49. Clomiphene ────────────────────────────
  {
    id: 'rx-clomiphene',
    catalogNumber: 49,
    name: 'Clomiphene',
    ingredients: 'Clomiphene citrate (SERM)',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'oral',
    dosage: '25–50 mg every other day',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', threshold: 400, description: 'Total T <400 ng/dL in male desiring fertility preservation' },
      { biomarker: 'LH', condition: 'below_functional', description: 'Low LH suggesting secondary hypogonadism' },
    ],
    targetBiomarkers: ['total_testosterone', 'free_testosterone', 'LH', 'FSH', 'estradiol'],
    targetGoals: ['hormonal', 'sexual_health'],
    pairsWith: ['rx-anastrozole', 'rx-hcg'],
    contraindications: ['Liver disease or elevated liver enzymes', 'Pituitary tumor', 'Primary hypogonadism (elevated LH/FSH)', 'History of visual disturbances on SERMs'],
    drugInteractions: ['No major drug interactions at standard dose', 'May affect estrogen-sensitive lab tests'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Total T and Free T at 4–6 weeks then q3mo', 'LH and FSH at 4–6 weeks', 'Estradiol q3mo (clomiphene raises E2)', 'Liver function at baseline and q6mo', 'Visual symptom screening q3mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Clomiphene citrate stimulates endogenous testosterone production via hypothalamic estrogen receptor blockade, raising LH/FSH output while preserving spermatogenesis.',
    citations: ['Katz DJ et al. BJU Int 2012;110(4):573-78', 'Wheeler KM et al. Curr Urol Rep 2019;20(10):62'],
    gender: 'male',
    clinicalNotes: 'Preferred over TRT when fertility preservation is a priority. EOD dosing reduces estradiol rebound. Monitor for visual disturbances (rare). Less effective than TRT for symptom relief. May elevate estradiol — add anastrozole if E2 >40.',
    rationale: 'Low testosterone in male desiring fertility preservation. Clomiphene stimulates endogenous HPG axis to raise testosterone while maintaining spermatogenesis.',
    pricing: { supplierCost: '$10–20/mo', monthlyPrice: '$39/mo' },
    status: 'active',
    sortOrder: 49,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 50. Enclomiphene ──────────────────────────
  {
    id: 'rx-enclomiphene',
    catalogNumber: 50,
    name: 'Enclomiphene',
    ingredients: 'Enclomiphene citrate (trans-isomer SERM)',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'oral',
    dosage: '12.5–25 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', threshold: 400, description: 'Total T <400 ng/dL in male desiring fertility preservation' },
      { biomarker: 'LH', condition: 'below_functional', description: 'Low LH suggesting secondary hypogonadism' },
    ],
    targetBiomarkers: ['total_testosterone', 'free_testosterone', 'LH', 'FSH', 'estradiol'],
    targetGoals: ['hormonal', 'sexual_health'],
    pairsWith: ['rx-anastrozole', 'rx-hcg'],
    contraindications: ['Liver disease or elevated liver enzymes', 'Pituitary tumor', 'Primary hypogonadism (elevated LH/FSH)', 'History of visual disturbances on SERMs'],
    drugInteractions: ['No major drug interactions at standard dose', 'May affect estrogen-sensitive lab tests'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Total T and Free T at 4–6 weeks then q3mo', 'LH and FSH at 4–6 weeks', 'Estradiol q3mo', 'Liver function at baseline and q6mo', 'Visual symptom screening q3mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Enclomiphene is the active trans-isomer of clomiphene, providing estrogen receptor antagonism at the hypothalamus without the estrogenic agonist activity of zuclomiphene, resulting in cleaner T elevation with fewer side effects.',
    citations: ['Wiehle RD et al. Clin Ther 2014;36(2):262-70', 'Kim ED et al. J Urol 2016;195(4 Suppl):e906'],
    gender: 'male',
    clinicalNotes: 'Preferred over clomiphene — pure trans-isomer without estrogenic zu-clomiphene. Daily dosing (vs EOD for clomiphene). Better side effect profile. Compounded or imported. Preserves fertility while raising T.',
    rationale: 'Low testosterone with fertility preservation goal. Enclomiphene provides cleaner SERM activity than clomiphene with fewer estrogenic side effects for endogenous T stimulation.',
    pricing: { supplierCost: '$30–60/mo', monthlyPrice: '$79/mo' },
    status: 'active',
    sortOrder: 50,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 51. HCG ───────────────────────────────────
  {
    id: 'rx-hcg',
    catalogNumber: 51,
    name: 'HCG',
    ingredients: 'Human chorionic gonadotropin (recombinant or urinary-derived)',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'subq',
    dosage: '500–1000 IU SubQ 2x/week',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'LH', condition: 'below_functional', description: 'Suppressed LH on TRT (testicular atrophy prevention)' },
    ],
    targetBiomarkers: ['LH', 'total_testosterone', 'free_testosterone', 'estradiol'],
    targetGoals: ['hormonal', 'sexual_health'],
    pairsWith: ['rx-testosterone-cypionate', 'rx-anastrozole'],
    contraindications: ['Prostate cancer', 'Androgen-dependent tumor', 'Precocious puberty', 'Ovarian hyperstimulation (female context)'],
    drugInteractions: ['No major direct drug interactions', 'May increase estradiol — monitor and use AI if needed'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Total T and Free T q3mo', 'Estradiol q3mo (HCG aromatizes)', 'Testicular volume assessment', 'Semen analysis if fertility is goal', 'Hematocrit q6mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'HCG mimics LH action on Leydig cells, maintaining intratesticular testosterone, testicular volume, and spermatogenesis during exogenous TRT.',
    citations: ['Coviello AD et al. J Clin Endocrinol Metab 2008;93(5):1874-80', 'Lee JA, Ramasamy R. Transl Androl Urol 2018;7(Suppl 3):S348-S352'],
    gender: 'male',
    clinicalNotes: 'Used concurrently with TRT to maintain testicular size and fertility. Aromatizes to estradiol — monitor and add anastrozole if E2 rises >40. Requires refrigeration after reconstitution. SubQ is equivalent to IM.',
    rationale: 'Suppressed LH from exogenous TRT causing testicular atrophy and/or fertility concerns. HCG maintains Leydig cell stimulation and intratesticular testosterone production.',
    pricing: { supplierCost: '$50–90/mo', monthlyPrice: '$129/mo' },
    status: 'active',
    sortOrder: 51,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 52. Pregnenolone ──────────────────────────
  {
    id: 'rx-pregnenolone',
    catalogNumber: 52,
    name: 'Pregnenolone',
    ingredients: 'Pregnenolone (micronized)',
    category: 'rx',
    subcategory: 'neurohormone',
    tier: 2,
    route: 'oral',
    dosage: '30–100 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'pregnenolone', condition: 'below_functional', description: 'Pregnenolone below functional range' },
    ],
    targetBiomarkers: ['pregnenolone', 'cortisol', 'dhea_s', 'progesterone'],
    targetGoals: ['cognition', 'energy', 'hormonal'],
    pairsWith: ['rx-dhea'],
    contraindications: ['Hormone-sensitive cancers', 'Seizure disorders (may lower threshold at high doses)', 'Severe hepatic impairment'],
    drugInteractions: ['Hormone therapies — additive hormonal effects', 'Sedatives / benzodiazepines — may enhance sedation', 'Anticonvulsants — potential interaction at high doses'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Pregnenolone level at 6–8 weeks then q6mo', 'DHEA-S and progesterone q6mo (downstream metabolites)', 'Cortisol q6mo', 'Cognitive function assessment'],
    evidenceLevel: 'emerging',
    evidenceSummary: 'Pregnenolone is the master precursor steroid hormone, supporting neurosteroid production, cognitive function, and downstream hormone synthesis. Clinical evidence is emerging for cognitive support and mood.',
    citations: ['Marx CE et al. Psychoneuroendocrinology 2009;34(Suppl 1):S91-S101', 'Flood JF et al. Brain Res 1992;570(1-2):99-104'],
    gender: 'any',
    clinicalNotes: 'Master precursor to all steroid hormones (progesterone, DHEA, cortisol, testosterone, estradiol). Supports cognitive function as a neurosteroid. Monitor downstream conversion pathways. Start low and titrate.',
    rationale: 'Low pregnenolone indicating depleted hormone precursor pools. Replacement supports cognitive function, mood, and provides substrate for downstream steroid hormone production.',
    pricing: { supplierCost: '$10–20/mo', monthlyPrice: '$39/mo' },
    status: 'active',
    sortOrder: 52,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 53. Sermorelin ────────────────────────────
  {
    id: 'rx-sermorelin',
    catalogNumber: 53,
    name: 'Sermorelin',
    ingredients: 'Sermorelin acetate (GHRH analog)',
    category: 'rx',
    subcategory: 'gh_peptide',
    tier: 2,
    route: 'subq',
    dosage: '200–300 mcg SubQ at bedtime',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'IGF_1', condition: 'below_functional', description: 'IGF-1 below age-adjusted functional range' },
    ],
    targetBiomarkers: ['IGF_1'],
    targetGoals: ['longevity', 'sleep', 'body_composition', 'energy'],
    pairsWith: [],
    contraindications: ['Active malignancy', 'Pituitary tumor / surgery', 'Pregnancy or nursing', 'Intracranial hypertension'],
    drugInteractions: ['Glucocorticoids — may blunt GH response', 'Insulin — may need dose adjustment (GH affects glucose)', 'Thyroid hormone — GH can increase T4-to-T3 conversion'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['IGF-1 at 8–12 weeks then q6mo', 'Fasting glucose q3mo (GH can increase glucose)', 'Joint symptoms assessment', 'Sleep quality assessment', 'HbA1c q6mo'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Sermorelin stimulates physiologic GH release from the anterior pituitary, increasing IGF-1 levels to support body composition, sleep quality, recovery, and tissue repair without supraphysiologic GH spikes.',
    citations: ['Walker RF. Clin Interv Aging 2006;1(4):307-16', 'Merriam GR et al. J Clin Endocrinol Metab 2001;86(4):1568-73'],
    gender: 'any',
    clinicalNotes: 'Inject SubQ at bedtime to synergize with natural nocturnal GH pulse. Empty stomach for at least 2 hours before injection. Avoid eating for 30 min after. Effects take 3–6 months. Must be refrigerated.',
    rationale: 'IGF-1 below functional range indicating suboptimal growth hormone axis. Sermorelin stimulates endogenous GH release to optimize recovery, sleep, body composition, and cellular repair.',
    pricing: { supplierCost: '$80–150/mo', monthlyPrice: '$199/mo' },
    status: 'active',
    sortOrder: 53,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 54. Ezetimibe ─────────────────────────────
  {
    id: 'rx-ezetimibe',
    catalogNumber: 54,
    name: 'Ezetimibe',
    ingredients: 'Ezetimibe (cholesterol absorption inhibitor)',
    category: 'rx',
    subcategory: 'lipid',
    tier: 2,
    route: 'oral',
    dosage: '10 mg daily',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'LDL', condition: 'above_functional', threshold: 130, description: 'LDL-C >130 mg/dL' },
      { biomarker: 'apoB', condition: 'above_functional', threshold: 100, description: 'apoB >100 mg/dL' },
    ],
    targetBiomarkers: ['LDL', 'apoB', 'total_cholesterol'],
    targetGoals: ['cardiovascular', 'longevity'],
    pairsWith: ['rx-rosuvastatin'],
    contraindications: ['Active liver disease or unexplained persistent transaminase elevation (if combined with statin)', 'Hypersensitivity to ezetimibe'],
    drugInteractions: ['Cyclosporine — increased ezetimibe levels', 'Fibrates — increased gallstone risk', 'Cholestyramine — reduced ezetimibe absorption; separate by 2+ hours', 'Warfarin — monitor INR when adding'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Lipid panel at 6–8 weeks then q3–6mo', 'Liver enzymes if combined with statin at baseline and q6mo', 'apoB q6mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Ezetimibe inhibits intestinal cholesterol absorption (NPC1L1), reducing LDL-C by 15–20%. IMPROVE-IT trial demonstrated additional cardiovascular event reduction when added to statin therapy.',
    citations: ['Cannon CP et al. N Engl J Med 2015;372:2387-97 (IMPROVE-IT)', 'Sudhop T et al. Circulation 2002;106(15):1943-48'],
    gender: 'any',
    clinicalNotes: 'First-line add-on to statin therapy when LDL goal not reached. Can also be used as monotherapy if statin-intolerant. Well-tolerated with minimal side effects. No food timing requirements.',
    rationale: 'Elevated LDL-C and/or apoB above cardiovascular risk thresholds. Ezetimibe reduces cholesterol absorption to provide additional LDL lowering, particularly effective in combination with statin therapy.',
    pricing: { supplierCost: '$5–12/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 54,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 55. Rosuvastatin ──────────────────────────
  {
    id: 'rx-rosuvastatin',
    catalogNumber: 55,
    name: 'Rosuvastatin',
    ingredients: 'Rosuvastatin calcium (HMG-CoA reductase inhibitor)',
    category: 'rx',
    subcategory: 'lipid',
    tier: 2,
    route: 'oral',
    dosage: '5–20 mg daily',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'LDL', condition: 'above_functional', threshold: 130, description: 'LDL-C >130 mg/dL' },
      { biomarker: 'apoB', condition: 'above_functional', threshold: 100, description: 'apoB >100 mg/dL' },
    ],
    targetBiomarkers: ['LDL', 'apoB', 'total_cholesterol', 'hs_crp', 'triglycerides'],
    targetGoals: ['cardiovascular', 'longevity'],
    pairsWith: ['rx-ezetimibe'],
    contraindications: ['Active liver disease or unexplained persistent transaminase elevation', 'Pregnancy (Category X)', 'Nursing mothers', 'Severe renal impairment at highest doses (40 mg)'],
    drugInteractions: ['Cyclosporine — contraindicated (markedly increases rosuvastatin levels)', 'Gemfibrozil — increased rhabdomyolysis risk', 'Warfarin — increased INR; monitor closely', 'Protease inhibitors (lopinavir/ritonavir) — increased statin levels', 'Antacids — separate by 2 hours', 'Niacin — increased myopathy risk at high doses'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Lipid panel at 6–8 weeks then q3–6mo', 'Liver enzymes (ALT/AST) at baseline, 12 weeks, then q6–12mo', 'CK if muscle symptoms develop', 'Fasting glucose / HbA1c q6mo (statins may increase glucose)', 'apoB q6mo'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Rosuvastatin is the most potent statin, reducing LDL-C by 45–55% at standard doses. JUPITER trial demonstrated 44% reduction in cardiovascular events in primary prevention.',
    citations: ['Ridker PM et al. N Engl J Med 2008;359:2195-207 (JUPITER)', 'Jones PH et al. Am J Cardiol 2003;92(2):152-60 (STELLAR)'],
    gender: 'any',
    clinicalNotes: 'Most potent statin — start at 5–10 mg. Evening dosing slightly preferred but not required (long half-life). Monitor for myalgias, elevated liver enzymes. CoQ10 supplementation may mitigate statin-related muscle symptoms. Watch for new-onset diabetes risk.',
    rationale: 'Elevated LDL-C and/or apoB indicating elevated cardiovascular risk. Rosuvastatin provides potent HMG-CoA reductase inhibition with proven cardiovascular event reduction.',
    pricing: { supplierCost: '$4–12/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 55,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 56. Bupropion ─────────────────────────────
  {
    id: 'rx-bupropion',
    catalogNumber: 56,
    name: 'Bupropion',
    ingredients: 'Bupropion hydrochloride extended-release (NDRI)',
    category: 'rx',
    subcategory: 'mental_health',
    tier: 2,
    route: 'oral',
    dosage: '150–300 mg XL daily in the morning',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'cortisol', condition: 'any_suboptimal', description: 'Depression, low motivation, or difficulty concentrating' },
    ],
    targetBiomarkers: ['cortisol'],
    targetGoals: ['cognition', 'energy', 'body_composition'],
    pairsWith: ['rx-buspirone'],
    contraindications: ['Seizure disorder or history of seizures', 'Bulimia or anorexia nervosa (increased seizure risk)', 'Abrupt discontinuation of alcohol or benzodiazepines', 'MAO inhibitor use within 14 days', 'Known allergy to bupropion'],
    drugInteractions: ['MAOIs — contraindicated; wait 14 days', 'CYP2D6 substrates (SSRIs, beta-blockers, antiarrhythmics) — bupropion inhibits 2D6', 'Alcohol — increased seizure risk', 'Medications that lower seizure threshold (antipsychotics, theophylline, systemic steroids)', 'Tamoxifen — bupropion inhibits 2D6 conversion to active metabolite'],
    allergenFlags: [],
    pregnancyCategory: 'C',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Mood and symptom assessment at 2 weeks, 4 weeks, then q3mo', 'Blood pressure q3mo (bupropion can raise BP)', 'Weight q3mo', 'Seizure risk factor assessment', 'Suicidality screening (black box warning <25 y/o)'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Bupropion is an NDRI antidepressant with proven efficacy for depression, smoking cessation, and seasonal affective disorder. Uniquely among antidepressants, it is weight-neutral or promotes weight loss and has no sexual side effects.',
    citations: ['Dhillon S et al. Drugs 2008;68(5):653-89', 'Stahl SM et al. J Clin Psychiatry 2004;65(10):1378-90'],
    gender: 'any',
    clinicalNotes: 'Activating antidepressant — take in AM to avoid insomnia. No sexual dysfunction (unlike SSRIs). May aid smoking cessation and focus. Avoid in seizure-prone patients. Do not crush XL formulation. Black box warning for suicidality in patients <25.',
    rationale: 'Depressive symptoms, low motivation, or difficulty concentrating. Bupropion provides norepinephrine-dopamine reuptake inhibition to improve mood, energy, and focus without sexual side effects or weight gain.',
    pricing: { supplierCost: '$8–20/mo', monthlyPrice: '$35/mo' },
    status: 'active',
    sortOrder: 56,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 57. Buspirone ─────────────────────────────
  {
    id: 'rx-buspirone',
    catalogNumber: 57,
    name: 'Buspirone',
    ingredients: 'Buspirone hydrochloride (5-HT1A partial agonist)',
    category: 'rx',
    subcategory: 'mental_health',
    tier: 2,
    route: 'oral',
    dosage: '5–15 mg 2x/day',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      { biomarker: 'cortisol', condition: 'above_functional', description: 'Elevated cortisol with anxiety symptoms' },
    ],
    targetBiomarkers: ['cortisol'],
    targetGoals: ['sleep', 'cognition'],
    pairsWith: ['rx-bupropion'],
    contraindications: ['MAO inhibitor use within 14 days', 'Severe hepatic impairment', 'Severe renal impairment'],
    drugInteractions: ['MAOIs — contraindicated; hypertensive crisis risk', 'CYP3A4 inhibitors (ketoconazole, erythromycin, grapefruit juice) — increased buspirone levels', 'CYP3A4 inducers (rifampin) — decreased buspirone levels', 'Haloperidol — increased haloperidol levels', 'Diazepam — increased diazepam levels (use caution)'],
    allergenFlags: [],
    pregnancyCategory: 'B',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Anxiety symptom assessment at 2 weeks, 4 weeks, then q3mo', 'Liver function at baseline if hepatic concern', 'Renal function at baseline if renal concern'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Buspirone is a non-benzodiazepine anxiolytic with no dependence, tolerance, or withdrawal risk. Effective for generalized anxiety disorder with onset of action in 1–2 weeks.',
    citations: ['Loane C, Politis M. Acta Neuropsychiatrica 2012;24(2):61-69', 'Chessick CA et al. Cochrane Database Syst Rev 2006;(1):CD006115'],
    gender: 'any',
    clinicalNotes: 'Non-addictive anxiolytic — no dependence or withdrawal. Takes 1–2 weeks for onset. Not effective as PRN — must take consistently. Split dosing AM/PM. Does not impair cognition or cause sedation at standard doses. Good option for patients wanting to avoid benzodiazepines.',
    rationale: 'Anxiety symptoms with elevated cortisol. Buspirone provides serotonergic anxiolysis without benzodiazepine risks of dependence, sedation, or cognitive impairment.',
    pricing: { supplierCost: '$5–12/mo', monthlyPrice: '$29/mo' },
    status: 'active',
    sortOrder: 57,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 58. Tretinoin ─────────────────────────────
  {
    id: 'rx-tretinoin',
    catalogNumber: 58,
    name: 'Tretinoin',
    ingredients: 'Tretinoin (all-trans retinoic acid)',
    category: 'rx',
    subcategory: 'skin_regeneration',
    tier: 2,
    route: 'topical',
    dosage: '0.025–0.1% cream/gel applied nightly to clean, dry skin',
    timing: 'PM_RECOVERY',
    triggers: [
      { biomarker: 'vitamin_a', condition: 'any_suboptimal', description: 'Skin aging, photoaging, or acne' },
    ],
    targetBiomarkers: ['vitamin_a'],
    targetGoals: ['longevity'],
    pairsWith: ['rx-minoxidil-oral'],
    contraindications: ['Pregnancy — CATEGORY X (teratogenic)', 'Planning pregnancy', 'Breastfeeding', 'Eczema or severe dermatitis in treatment area', 'Concurrent use of other retinoids (isotretinoin)'],
    drugInteractions: ['Other topical retinoids — excessive irritation', 'Benzoyl peroxide — may degrade tretinoin; apply at different times', 'AHA / BHA exfoliants — increased irritation', 'Photosensitizing drugs (tetracyclines, fluoroquinolones) — increased photosensitivity', 'Topical corticosteroids — may reduce retinoid efficacy long-term'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Skin irritation assessment at 2 weeks and 4 weeks', 'Pregnancy test before initiation in women of childbearing age', 'Effective contraception confirmed', 'Photosensitivity counseling'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Tretinoin is the gold standard topical retinoid with decades of evidence for photoaging reversal, collagen synthesis stimulation, and acne treatment. Reduces fine wrinkles, hyperpigmentation, and rough skin texture.',
    citations: ['Kang S et al. J Am Acad Dermatol 2005;52(1):25-33', 'Mukherjee S et al. Clin Interv Aging 2006;1(4):327-48'],
    gender: 'any',
    clinicalNotes: 'Start with 0.025% and titrate up to reduce irritation (retinization period 2–6 weeks). Apply pea-sized amount nightly. MUST use daily SPF 30+. Avoid in pregnancy — Category X. Initial purging phase is normal for acne patients. Moisturize after application.',
    rationale: 'Skin aging, photoaging, or acne warranting prescription-strength retinoid therapy. Tretinoin stimulates collagen synthesis, accelerates cell turnover, and reverses UV-induced skin damage.',
    pricing: { supplierCost: '$10–25/mo', monthlyPrice: '$49/mo' },
    status: 'active',
    sortOrder: 58,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 59. Dutasteride ───────────────────────────
  {
    id: 'rx-dutasteride',
    catalogNumber: 59,
    name: 'Dutasteride',
    ingredients: 'Dutasteride (dual 5-alpha reductase inhibitor, type I and II)',
    category: 'rx',
    subcategory: 'hormone_male',
    tier: 2,
    route: 'oral',
    dosage: '0.5 mg daily',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'DHT', condition: 'above_functional', description: 'Elevated DHT with BPH or androgenetic alopecia' },
      { biomarker: 'PSA', condition: 'above_functional', description: 'Elevated PSA with BPH symptoms' },
    ],
    targetBiomarkers: ['DHT', 'PSA', 'total_testosterone'],
    targetGoals: ['prostate', 'body_composition'],
    pairsWith: ['rx-finasteride', 'rx-minoxidil-oral', 'rx-tadalafil'],
    contraindications: ['Female patients (teratogenic)', 'Pregnancy or potential pregnancy in partner (donate blood restriction 6 months)', 'Hepatic impairment', 'Children'],
    drugInteractions: ['CYP3A4 inhibitors (ketoconazole, ritonavir) — increased dutasteride levels', 'PSA values will be reduced ~50% — adjust interpretation accordingly', 'No clinically significant interactions with most medications'],
    allergenFlags: ['Gelatin capsule'],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['PSA at baseline then q6–12mo (adjust for 50% reduction)', 'DHT level at 3 months (optional)', 'Prostate symptom score (IPSS) q6mo', 'Sexual function assessment q6mo', 'Liver function at baseline'],
    evidenceLevel: 'strong',
    evidenceSummary: 'Dutasteride inhibits both type I and type II 5-alpha reductase, reducing serum DHT by >90% (vs ~70% for finasteride). FDA-approved for BPH with off-label use for hair loss.',
    citations: ['Roehrborn CG et al. J Urol 2002;167(3):1334-40 (ARIA)', 'Olsen EA et al. J Am Acad Dermatol 2006;55(6):1014-23'],
    gender: 'male',
    clinicalNotes: 'Stronger than finasteride — inhibits both 5AR isoforms. Very long half-life (5 weeks) — effects persist weeks after discontinuation. Cannot donate blood for 6 months after use. Women of childbearing age must not handle capsules. Higher rate of sexual side effects vs finasteride.',
    rationale: 'Elevated DHT with BPH symptoms and/or hair loss requiring more potent 5-alpha reductase inhibition than finasteride. Dutasteride provides dual-isoform blockade for maximal DHT reduction.',
    pricing: { supplierCost: '$10–20/mo', monthlyPrice: '$39/mo' },
    status: 'active',
    sortOrder: 59,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 60. Nandrolone Decanoate ──────────────────
  {
    id: 'rx-nandrolone-decanoate',
    catalogNumber: 60,
    name: 'Nandrolone Decanoate',
    ingredients: 'Nandrolone decanoate in sesame oil',
    category: 'rx',
    subcategory: 'hrt_male',
    tier: 2,
    route: 'im',
    dosage: '50–100 mg IM every 2 weeks',
    timing: 'AM_ENERGY',
    triggers: [
      { biomarker: 'total_testosterone', condition: 'below_functional', description: 'Muscle wasting or joint degeneration with low anabolic markers' },
    ],
    targetBiomarkers: ['total_testosterone', 'hematocrit', 'albumin'],
    targetGoals: ['body_composition', 'longevity'],
    pairsWith: ['rx-testosterone-cypionate'],
    contraindications: ['Prostate cancer', 'Breast cancer in males', 'Nephrotic syndrome with severe renal impairment', 'Hypercalcemia', 'Pregnancy (Category X)', 'Polycythemia (hematocrit >54%)'],
    drugInteractions: ['Anticoagulants (warfarin) — enhanced anticoagulant effect; monitor INR', 'Insulin / oral hypoglycemics — may decrease glucose; adjust doses', 'Corticosteroids — increased edema risk', 'Cyclosporine — increased cyclosporine levels'],
    allergenFlags: ['Sesame oil (carrier)', 'Benzyl alcohol (preservative)'],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Hematocrit q3mo (phlebotomy if >54%)', 'Lipid panel q6mo', 'Liver function q6mo', 'PSA q6–12mo', 'Joint symptom assessment q3mo', 'Bone density if long-term use'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Nandrolone is an anabolic steroid with favorable anabolic-to-androgenic ratio, FDA-approved for anemia of renal insufficiency. Used off-label for muscle wasting and joint/collagen repair.',
    citations: ['Johansen KL et al. Am J Kidney Dis 1999;33(6):1061-68', 'Sattler FR et al. J Clin Endocrinol Metab 1999;84(4):1268-76'],
    gender: 'male',
    clinicalNotes: 'Lower androgenic side effects than testosterone. Beneficial for joint pain via collagen synthesis stimulation. Monitor hematocrit closely. Long-acting ester allows biweekly dosing. May suppress endogenous T — use with TRT base. Sesame oil allergy check required.',
    rationale: 'Muscle wasting and/or joint degeneration requiring anabolic support beyond testosterone. Nandrolone provides high anabolic activity with favorable joint-protective collagen synthesis effects.',
    pricing: { supplierCost: '$40–80/mo', monthlyPrice: '$99/mo' },
    status: 'active',
    sortOrder: 60,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ── 61. Oxandrolone ───────────────────────────
  {
    id: 'rx-oxandrolone',
    catalogNumber: 61,
    name: 'Oxandrolone',
    ingredients: 'Oxandrolone (anabolic steroid)',
    category: 'rx',
    subcategory: 'clinical',
    tier: 2,
    route: 'oral',
    dosage: '10–20 mg/day in 2 divided doses',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      { biomarker: 'albumin', condition: 'below_functional', description: 'Low albumin / protein indicating muscle wasting or catabolic state' },
    ],
    targetBiomarkers: ['albumin', 'total_testosterone', 'hematocrit'],
    targetGoals: ['body_composition', 'longevity'],
    pairsWith: [],
    contraindications: ['Prostate cancer', 'Breast cancer in males', 'Nephrotic syndrome', 'Hypercalcemia', 'Pregnancy (Category X)', 'Severe hepatic impairment (hepatotoxic — C-17 alpha alkylated)'],
    drugInteractions: ['Anticoagulants (warfarin) — significantly enhanced effect; reduce dose and monitor INR closely', 'Insulin / oral hypoglycemics — hypoglycemia risk; reduce doses', 'Corticosteroids — increased edema risk', 'Hepatotoxic medications — additive liver toxicity'],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Liver enzymes (ALT/AST) at baseline, 2 weeks, monthly for 3 months, then q3mo', 'Lipid panel q3mo (oxandrolone can significantly lower HDL)', 'Hematocrit q3mo', 'Fasting glucose q3mo', 'Peliosis hepatis screening', 'PSA q6mo in males'],
    evidenceLevel: 'moderate',
    evidenceSummary: 'Oxandrolone is an oral anabolic steroid FDA-approved for weight regain after severe illness, surgery, or chronic infection. Strong anabolic effect with relatively mild androgenic activity.',
    citations: ['Demling RH, DeSanti L. J Burn Care Rehabil 2003;24(1):9-18', 'Hart DW et al. Ann Surg 2001;233(4):556-64'],
    gender: 'any',
    clinicalNotes: 'Short-term use preferred (6–12 weeks) due to hepatotoxicity risk (C-17 alkylated). Monitor liver enzymes closely — discontinue if ALT/AST >3x ULN. Will suppress endogenous testosterone in males. Significantly lowers HDL — monitor lipids. FDA-approved for wasting conditions.',
    rationale: 'Muscle wasting, catabolic state, or post-surgical recovery requiring anabolic support. Oxandrolone provides potent nitrogen retention and muscle preservation with FDA approval for wasting conditions.',
    pricing: { supplierCost: '$60–120/mo', monthlyPrice: '$149/mo' },
    status: 'active',
    sortOrder: 61,
    updatedAt: '2025-01-01T00:00:00Z',
    updatedBy: 'system-seed',
  },
];
